import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { dashboardAPI, goalsAPI } from '../../api';
import { Card, StatCard, Spinner, ProgressBar, Button } from '../../components/common';
import { cn } from '../../utils';
import {
  Users, Calendar, Clock, Star, Gift, AlertTriangle, Flame, 
  Shield, Settings, User, TrendingUp, Target, Trophy
} from 'lucide-react';

// Admin Dashboard
export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await dashboardAPI.admin();
        setStats(response.data);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <p className="text-gray-500">Overview of your gym's gamification metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats?.total_users || 0} color="primary" />
        <StatCard icon={Users} label="Active This Week" value={stats?.active_users_week || 0} color="blue" />
        <StatCard icon={Calendar} label="Total Sessions" value={stats?.total_sessions || 0} color="purple" />
        <StatCard icon={Clock} label="Pending Confirmations" value={stats?.pending_confirmations || 0} color="amber" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Star} label="Points Awarded" value={stats?.total_points_awarded?.toLocaleString() || 0} color="primary" />
        <StatCard icon={Gift} label="Pending Redemptions" value={stats?.pending_redemptions || 0} color="amber" />
        <StatCard icon={AlertTriangle} label="Open Flags" value={stats?.open_flags || 0} color="red" />
        <StatCard icon={Flame} label="Avg Streak" value={stats?.avg_streak || 0} subtext="days" color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Top Streaks</h3>
          {stats?.top_streaks?.length > 0 ? (
            <div className="space-y-3">
              {stats.top_streaks.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                      idx === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                    )}>
                      {idx + 1}
                    </div>
                    <span>{item.user_name}</span>
                  </div>
                  <div className="flex items-center gap-1 text-amber-600 font-medium">
                    <Flame className="w-4 h-4" />
                    {item.streak}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No streak data yet</p>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/gamification">
              <Button variant="outline" className="w-full justify-start">
                <Settings className="w-4 h-4 mr-2" /> Configure Rules
              </Button>
            </Link>
            <Link to="/rewards">
              <Button variant="outline" className="w-full justify-start">
                <Gift className="w-4 h-4 mr-2" /> Manage Rewards
              </Button>
            </Link>
            <Link to="/flags">
              <Button variant="outline" className="w-full justify-start">
                <AlertTriangle className="w-4 h-4 mr-2" /> Review Flags
              </Button>
            </Link>
            <Link to="/integrity">
              <Button variant="outline" className="w-full justify-start">
                <Shield className="w-4 h-4 mr-2" /> Verify Integrity
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Trainer Dashboard
export const TrainerDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await dashboardAPI.trainer();
        setStats(response.data);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="trainer-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Trainer Dashboard</h1>
        <p className="text-gray-500">Your training activity overview</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="My Clients" value={stats?.total_clients || 0} color="primary" />
        <StatCard icon={Calendar} label="Sessions This Week" value={stats?.sessions_this_week || 0} color="blue" />
        <StatCard icon={Clock} label="Pending Confirmations" value={stats?.pending_confirmations || 0} color="amber" />
        <StatCard icon={Flame} label="Client Avg Streak" value={stats?.client_avg_streak || 0} color="amber" />
      </div>

      <Card className="p-6">
        <h3 className="font-semibold mb-4">Top Performing Clients</h3>
        {stats?.top_performing_clients?.length > 0 ? (
          <div className="space-y-3">
            {stats.top_performing_clients.map((client, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-primary-600" />
                  </div>
                  <span className="font-medium">{client.user_name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-semibold text-primary-600">{client.points} pts</p>
                    <p className="text-xs text-gray-500">{client.streak} day streak</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No clients assigned yet</p>
        )}
      </Card>
    </div>
  );
};

// Client Dashboard
export const ClientDashboard = () => {
  const [stats, setStats] = useState(null);
  const [activeGoals, setActiveGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [statsRes, goalsRes] = await Promise.all([
          dashboardAPI.client(),
          goalsAPI.list({ active_only: true }).catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setActiveGoals(goalsRes.data || []);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Welcome Back!</h1>
        <p className="text-gray-500">Here's your fitness journey progress</p>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-primary-500 to-primary-700 text-white">
          <Star className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{stats?.total_points?.toLocaleString() || 0}</p>
          <p className="text-sm opacity-80">Total Points</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-500 to-orange-600 text-white">
          <Flame className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{stats?.current_streak || 0}</p>
          <p className="text-sm opacity-80">Day Streak</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-primary-600" />
            <span className="text-sm text-gray-500">Level</span>
          </div>
          <p className="text-2xl font-bold">{stats?.current_level || 1}</p>
          <p className="text-sm text-gray-500">{stats?.level_name || 'Newcomer'}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-500">This Month</span>
          </div>
          <p className="text-2xl font-bold">{stats?.sessions_this_month || 0}</p>
          <p className="text-sm text-gray-500">Sessions</p>
        </Card>
      </div>

      {/* Active Goals */}
      {activeGoals.length > 0 && (
        <Link to="/progress/goals">
          <Card className="p-6 hover:border-primary-200/40 transition-colors cursor-pointer group">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="hero-kicker">Active targets</span>
                <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
                  Goals
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-display text-2xl text-[var(--color-text)]">
                  {activeGoals.length}
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                  active
                </span>
                <Target className="w-5 h-5 text-primary-200 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
            <div className="space-y-3">
              {activeGoals.slice(0, 3).map((g) => (
                <div key={g.id} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--color-text)] truncate">
                      {g.metric_label}
                      <span className="text-[var(--color-text-muted)] ml-1.5">
                        → {g.target_value.toFixed(1)}{g.unit}
                      </span>
                    </span>
                    <span className={`font-mono ${g.progress_pct >= 100 ? 'text-primary-100' : g.progress_pct >= 50 ? 'text-primary-200' : 'text-amber-200'}`}>
                      {Math.round(g.progress_pct)}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-100"
                      style={{ width: `${Math.min(100, Math.max(0, g.progress_pct))}%` }}
                    />
                  </div>
                </div>
              ))}
              {activeGoals.length > 3 && (
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] text-center pt-1">
                  +{activeGoals.length - 3} more
                </div>
              )}
            </div>
          </Card>
        </Link>
      )}

      {/* Progress to Next Level */}
      {stats?.next_milestone && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Progress to Next Level</h3>
            <span className="text-sm text-gray-500">
              {stats.next_milestone.points_needed} pts to {stats.next_milestone.name}
            </span>
          </div>
          <ProgressBar 
            value={stats.total_points} 
            max={stats.total_points + stats.next_milestone.points_needed} 
          />
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/sessions">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
            <Calendar className="w-6 h-6 text-blue-600 mb-2" />
            <p className="font-medium">Sessions</p>
            <p className="text-xs text-gray-500">View & confirm</p>
          </Card>
        </Link>
        <Link to="/rewards">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
            <Gift className="w-6 h-6 text-purple-600 mb-2" />
            <p className="font-medium">Rewards</p>
            <p className="text-xs text-gray-500">{stats?.available_rewards || 0} available</p>
          </Card>
        </Link>
        <Link to="/challenges">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
            <Target className="w-6 h-6 text-amber-600 mb-2" />
            <p className="font-medium">Challenges</p>
            <p className="text-xs text-gray-500">{stats?.active_challenges || 0} active</p>
          </Card>
        </Link>
        <Link to="/leaderboard">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
            <Trophy className="w-6 h-6 text-yellow-600 mb-2" />
            <p className="font-medium">Leaderboard</p>
            <p className="text-xs text-gray-500">See rankings</p>
          </Card>
        </Link>
      </div>
    </div>
  );
};

// Dashboard Router Component
export const Dashboard = () => {
  const { user } = useAuth();

  // Master admin gets the admin dashboard (platform-wide stats). Their org_id
  // still resolves to their own gym in the BE, but the AdminDashboard fetches
  // /api/admin/dashboard which is master_admin-gated, so the stats are
  // platform-wide rather than scoped to one gym.
  if (user?.role === 'admin' || user?.role === 'master_admin') return <AdminDashboard />;
  if (user?.role === 'trainer') return <TrainerDashboard />;
  return <ClientDashboard />;
};

export default Dashboard;
